import AlarmOffIcon from '@mui/icons-material/AlarmOff';
import DoneIcon from '@mui/icons-material/Done';
import PriorityHighIcon from '@mui/icons-material/PriorityHigh';
import {
  Box,
  Button,
  CircularProgress,
  Collapse,
  Paper,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  useScrollTrigger,
} from '@mui/material';
import * as React from 'react';
import { useContext, useEffect, useState } from 'react';
import { usePolling } from '../../../../hooks/usePolling';
import PathContext from '../../../../shared-components/contexts/PathContext';
import { IExport } from '../../../../shared-interfaces/IExport';
import ExportService from '../Services/ExportService';
import { ExportStatus } from '../Types/ExportStatus';
import AddIcon from '@mui/icons-material/Add';

interface IExportDownloadContainerProps {
  showExportDownloadContainer: boolean;
}

const RowLoadingContainer = () => {
  return (
    <TableRow>
      <TableCell width={450}>
        <Skeleton variant="text" width="80%" />
        <Skeleton variant="text" width="50%" />
        <Skeleton variant="text" width="50%" />
      </TableCell>
      <TableCell>
        <Skeleton variant="text" width="80%" />
        <Skeleton variant="text" width="60%" />
      </TableCell>
      <TableCell>
        <Skeleton variant="text" width="80%" />
        <Skeleton variant="text" width="60%" />
      </TableCell>
      <TableCell>
        <Skeleton variant="text" width="90%" />
        <Skeleton variant="text" width="60%" />
      </TableCell>
      <TableCell>
        <Box display="flex" justifyContent="center">
          <Skeleton variant="rectangular" width={25} height={25} />
        </Box>
      </TableCell>
    </TableRow>
  );
};

const StatusIndicator = (props: { status: string }) => {
  if (props.status.toString() === ExportStatus.PENDING) {
    return (
      <Tooltip
        title="Large files may take a long time to process. Check this page later for updates."
        placement="top"
        arrow
      >
        <CircularProgress size={25} color="primary" />
      </Tooltip>
    );
  } else if (props.status.toString() === ExportStatus.COMPLETE) {
    return (
      <Tooltip
        title="Export Ready. Click on the filename to download."
        placement="top"
        arrow
      >
        <DoneIcon color="primary" />
      </Tooltip>
    );
  } else if (props.status.toString() === ExportStatus.EXPIRED) {
    return (
      <Tooltip title="Export Expired" placement="top" arrow>
        <AlarmOffIcon color="error" />
      </Tooltip>
    );
  } else {
    return (
      <Tooltip title="An error has occured" placement="top" arrow>
        <PriorityHighIcon color="error" />
      </Tooltip>
    );
  }
};

const ExportDownloadContainer = (props: IExportDownloadContainerProps) => {
  const pathContext = useContext(PathContext);
  const [isLoading, setIsLoading] = useState(true);
  const [availableDownloads, setAvailableDownloads] = useState<IExport[]>([]);
  const scrollTrigger = useScrollTrigger();

  useEffect(() => {
    if (props.showExportDownloadContainer) {
      ExportService.getExportStatuses()
        .then(e => {
          setAvailableDownloads(e);
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  }, [props.showExportDownloadContainer]);

  useEffect(() => {
    // Scroll back to top when the ExportDownloadContainer is opened
    if (props.showExportDownloadContainer && scrollTrigger) {
      const anchor = document.querySelector('#user-management-header');

      if (anchor) {
        anchor.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  }, [props.showExportDownloadContainer]);

  usePolling(async () => {
    // Only poll while the container is open & there are pending exports
    if (
      props.showExportDownloadContainer &&
      availableDownloads.some(x => x.status === 'PENDING')
    ) {
      ExportService.getExportStatuses().then(e => {
        setAvailableDownloads(e);
      });
    }
  }, 5000);

  const handleQueueExport = () => {
    ExportService.startExport().then(e => {
      setAvailableDownloads([e, ...availableDownloads]);
    });
  };

  const handleDownloadExport = (exportId: string, fileName: string) => {
    ExportService.getExportStatus(exportId).then(status => {
      fetch(status.download?.url ?? '')
        .then(resp => resp.blob())
        .then(blob => {
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.style.display = 'none';
          a.href = url;
          a.download = fileName;
          document.body.appendChild(a);
          a.click();
          window.URL.revokeObjectURL(url);
        });
    });
  };

  return (
    <Collapse in={props.showExportDownloadContainer}>
      <Box
        display="flex"
        height="350px"
        style={{
          overflow: 'auto',
          borderBottom: '1px solid rgb(220, 220, 220)',
        }}
        flexDirection="column"
      >
        <Box
          display="flex"
          justifyContent="flex-end"
          width="100%"
          sx={{ padding: '8px' }}
        >
          <Button onClick={handleQueueExport} startIcon={<AddIcon />}>
            New Export
          </Button>
        </Box>
        <Box>
          <Paper>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>
                    <Typography>Filename</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography>Created On</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography>Expires On</Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Typography>Status</Typography>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {isLoading
                  ? [...Array(5)].map((e, i) => <RowLoadingContainer key={i} />)
                  : availableDownloads &&
                    availableDownloads.map(x => (
                      <TableRow key={x.exportId}>
                        <TableCell
                          style={{
                            overflowWrap: 'anywhere',
                          }}
                        >
                          {x.status === ExportStatus.COMPLETE ? (
                            <a
                              href={`${pathContext}/api/management/notation/download/${x.exportId}`}
                              onClick={e => {
                                e.preventDefault();
                                handleDownloadExport(x.exportId, x.fileName);
                              }}
                            >
                              <Typography>{x.fileName}</Typography>
                            </a>
                          ) : x.status === ExportStatus.PENDING ? (
                            <Skeleton width={200} />
                          ) : (
                            x.fileName
                          )}
                        </TableCell>
                        <TableCell>
                          <Typography>
                            {`${x.createdOn.toLocaleDateString()} ${x.createdOn.toLocaleTimeString(
                              [],
                              {
                                hour: '2-digit',
                                minute: '2-digit',
                              },
                            )}`}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography>
                            {x.expiresOn.toLocaleDateString()}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <StatusIndicator status={x.status} />
                        </TableCell>
                      </TableRow>
                    ))}
              </TableBody>
            </Table>
            {!isLoading && availableDownloads.length === 0 && (
              <Box
                height="200px"
                display="flex"
                justifyContent="center"
                alignItems="center"
              >
                <Typography variant="h2">
                  There are currently no exports.
                </Typography>
              </Box>
            )}
          </Paper>
        </Box>
      </Box>
    </Collapse>
  );
};

export default ExportDownloadContainer;
